/* A STAT BLOCK, AT THE TABLE.
 *
 * The console can act on a creature and cannot show one. "What does a goblin's
 * Nimble Escape actually say?" means another tab, which is the context switch
 * this product exists to remove.
 *
 * WHERE THE TEXT COMES FROM. Nothing new is fetched. When the Compass sends a
 * creature to Roll20 it hands over a document whose `attribs` are the whole
 * parsed statblock as classic ogl5e sheet rows, and the connector already keeps
 * them -- it just threw everything away except the actions. This reads the rest
 * back.
 *
 * THE PARSING IS HERE, NOT IN THE CONNECTOR, on purpose. The connector returns
 * the rows; the site makes sense of them. That way a mistake in reading a
 * statblock is fixed by a deploy, which takes seconds, rather than by the DM
 * reinstalling an extension and reloading every open tab. The connector should
 * hold transport and nothing that has an opinion.
 */
export type Row = [string, string, string?] | { name: string; current?: string; max?: string };

export interface Ability { key: string; label: string; score: number | null; mod: number | null }
export interface Entry { name: string; text: string }
export interface Section { key: string; title: string; entries: Entry[] }
export interface StatBlock {
  name: string;
  /** "Medium humanoid (goblinoid)" */
  type: string;
  ac: number | null;
  acNote: string;
  hp: number | null;
  hpFormula: string;
  speed: string;
  abilities: Ability[];
  saves: string;
  skills: string;
  senses: string;
  languages: string;
  cr: string;
  xp: string;
  resistances: string;
  immunities: string;
  vulnerabilities: string;
  conditionImmunities: string;
  /** how many legendary actions a round, and the preamble that explains them */
  legendary: number;
  legendaryNote: string;
  sections: Section[];
  /** true when the rows carried a header at all; false means actions only */
  hasHeader: boolean;
}

const ABILITIES: { key: string; label: string; attrib: string }[] = [
  { key: "str", label: "STR", attrib: "strength" },
  { key: "dex", label: "DEX", attrib: "dexterity" },
  { key: "con", label: "CON", attrib: "constitution" },
  { key: "int", label: "INT", attrib: "intelligence" },
  { key: "wis", label: "WIS", attrib: "wisdom" },
  { key: "cha", label: "CHA", attrib: "charisma" },
];

/* The Roll20 repeating families, in the order a statblock is read aloud. A
 * family the sheet does not use simply has no entries and is dropped. */
const FAMILIES: { family: string; title: string }[] = [
  { family: "npctrait", title: "Traits" },
  { family: "npcaction", title: "Actions" },
  { family: "npcbonusaction", title: "Bonus actions" },
  { family: "npcreaction", title: "Reactions" },
  { family: "npcaction-l", title: "Legendary actions" },
];

const asPairs = (rows: Row[] | null | undefined): Map<string, { current: string; max: string }> => {
  const m = new Map<string, { current: string; max: string }>();
  for (const row of rows || []) {
    if (Array.isArray(row)) { if (row[0]) m.set(String(row[0]), { current: String(row[1] ?? ""), max: String(row[2] ?? "") }); }
    else if (row && row.name) m.set(String(row.name), { current: String(row.current ?? ""), max: String(row.max ?? "") });
  }
  return m;
};

/** A number, or null when the row is absent OR blank.
 *
 *  Blank is NOT zero. An armour class of 0 and an unknown armour class look the
 *  same to Number(""), which is 0 and passes every "is this a finite number?"
 *  guard -- the trap this codebase has been bitten by seven times. */
export function num(v: string | undefined | null): number | null {
  if (v === undefined || v === null) return null;
  const t = String(v).trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const mod = (score: number | null): number | null => (score === null ? null : Math.floor((score - 10) / 2));
export const signed = (n: number | null): string => (n === null ? "—" : n >= 0 ? `+${n}` : String(n));

/* A list the sheet keeps as one flagged row per entry: npc_dex_save with
   npc_dex_save_flag, npc_stealth with npc_stealth_flag. Read back into the
   "Dex +4, Wis +2" line a statblock actually prints. */
function savesLine(p: Map<string, { current: string; max: string }>): string {
  const out: string[] = [];
  for (const a of ABILITIES) {
    const v = p.get(`npc_${a.key}_save`);
    const n = num(v?.current);
    if (n !== null) out.push(`${a.label[0]}${a.label.slice(1).toLowerCase()} ${signed(n)}`);
  }
  return out.join(", ");
}

/* Skills are not enumerable from the rows alone -- the sheet names them
   npc_<skill> with no marker that says "this is a skill" -- so they are found by
   their flag companion, which only skills have. */
function skillsLine(p: Map<string, { current: string; max: string }>): string {
  const out: { name: string; mod: number }[] = [];
  for (const [key, val] of p) {
    const m = /^npc_([a-z_]+)_flag$/.exec(key);
    if (!m) continue;
    const base = m[1];
    if (/_save$/.test(base) || base === "name" || base === "options") continue;
    const n = num(p.get(`npc_${base}`)?.current);
    if (n === null) continue;
    if (!val.current || val.current === "0") continue;
    out.push({ name: base.split("_").map((w) => w.slice(0, 1).toUpperCase() + w.slice(1)).join(" "), mod: n });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name)).map((s) => `${s.name} ${signed(s.mod)}`).join(", ");
}

/** Read a creature's statblock out of the Roll20 attribute rows the Compass
 *  wrote. Rows it does not recognise are ignored, and a missing header gives a
 *  block that is still worth reading: the actions. */
export function fromAttribs(rows: Row[] | null | undefined, fallbackName = ""): StatBlock {
  const p = asPairs(rows);
  const get = (k: string) => (p.get(k)?.current || "").trim();

  const grouped = new Map<string, Map<string, Entry>>();
  for (const [key, val] of p) {
    const m = /^repeating_(npctrait|npcaction|npcbonusaction|npcreaction|npcaction-l)_([^_]+)_(name|desc|description)$/.exec(key);
    if (!m) continue;
    const fam = grouped.get(m[1]) || new Map<string, Entry>();
    const e = fam.get(m[2]) || { name: "", text: "" };
    if (m[3] === "name") e.name = val.current;
    else if (!e.text) e.text = val.current;
    fam.set(m[2], e);
    grouped.set(m[1], fam);
  }
  const sections: Section[] = FAMILIES
    .map((f) => ({ key: f.family, title: f.title, entries: [...(grouped.get(f.family)?.values() || [])].filter((e) => e.name) }))
    .filter((s) => s.entries.length);

  const hpRow = p.get("hp");
  return {
    name: get("npc_name") || fallbackName,
    type: get("npc_type"),
    ac: num(get("npc_ac")),
    acNote: get("npc_actype"),
    /* The sheet keeps current in `current` and maximum in `max`; a statblock
       prints the maximum, so prefer it and fall back to current. */
    hp: num(hpRow?.max) ?? num(hpRow?.current),
    hpFormula: get("npc_hpformula"),
    speed: get("npc_speed"),
    abilities: ABILITIES.map((a) => {
      const score = num(get(a.attrib));
      /* The sheet stores its own modifier; trust it when it is there, because a
         homebrew creature may have one that is not derived from the score. */
      const stored = num(get(`${a.attrib}_mod`));
      return { key: a.key, label: a.label, score, mod: stored ?? mod(score) };
    }),
    saves: savesLine(p),
    skills: skillsLine(p),
    senses: get("npc_senses"),
    languages: get("npc_languages"),
    cr: get("npc_challenge"),
    xp: get("npc_xp"),
    resistances: get("npc_resistances"),
    immunities: get("npc_immunities"),
    vulnerabilities: get("npc_vulnerabilities"),
    conditionImmunities: get("npc_condition_immunities"),
    legendary: num(get("npc_legendary_actions")) ?? 0,
    legendaryNote: get("npc_legendary_actions_desc"),
    sections,
    hasHeader: Boolean(get("npc_ac") || get("npc_type") || get("npc_challenge") || get("npc_speed")),
  };
}

/** Build a block from the {name, text, family} actions the connector has always
 *  returned, for a creature whose rows predate the header. Actions only, and
 *  `hasHeader` says so rather than printing a block of dashes. */
export function fromActions(name: string, actions: { name: string; text: string; family?: string }[]): StatBlock {
  const rows: Row[] = [];
  actions.forEach((a, i) => {
    const fam = a.family && FAMILIES.some((f) => f.family === a.family) ? a.family : "npcaction";
    rows.push([`repeating_${fam}_r${i}_name`, a.name]);
    rows.push([`repeating_${fam}_r${i}_description`, a.text || ""]);
  });
  return { ...fromAttribs(rows, name), name };
}

/** The one-line summary a tracker row can show without opening anything. */
export function summary(b: StatBlock): string {
  const bits = [b.type, b.ac === null ? "" : `AC ${b.ac}`, b.hp === null ? "" : `${b.hp} hp`, b.cr ? `CR ${b.cr}` : ""];
  return bits.filter(Boolean).join(" · ");
}

/** Attack-shaped action text carries its own to-hit and damage; a reader wants
 *  the first line emphasised the way a printed block does. Returns null when the
 *  text is ordinary prose. */
export function attackLine(text: string): string | null {
  const m = /^(Melee|Ranged|Melee or Ranged)\s+(Weapon|Spell)\s+Attack:/i.exec(String(text || "").trim());
  return m ? m[0] : null;
}
